import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, resolveComponent as _resolveComponent, Fragment as _Fragment, normalizeClass as _normalizeClass, withCtx as _withCtx, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex text-info mb-s3"
}
const _hoisted_2 = { class: "sm:flex align-items-stretch gutter-s4" }
const _hoisted_3 = { class: "flex flex-grow-1 flex-column sm:flex-column-reverse gutter-y-s2" }
const _hoisted_4 = { class: "flex align-items-center justify-content-end sm:justify-content-between mb-s3" }
const _hoisted_5 = {
  key: 0,
  class: "caption"
}
const _hoisted_6 = { class: "input-sub-value" }
const _hoisted_7 = { class: "sm:w-18rem" }
const _hoisted_8 = { class: "h-full sm:flex sm:flex-column-reverse justify-content-between gutter-y-s3 sm:gutter-s0" }
const _hoisted_9 = { class: "underline mr-s1" }
const _hoisted_10 = {
  key: 1,
  class: "flex text-sm align-items-center"
}
const _hoisted_11 = {
  key: 0,
  class: "text-sm text-right text-warning mt-s1"
}
const _hoisted_12 = /*#__PURE__*/_createElementVNode("br", null, null, -1)

import BigNumber from 'bignumber.js';
import _ from 'lodash';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { formatNumbersMixin } from '@/mixins/format-numbers.mixin';
import { useSingleSideWithdraw } from '@/store/modules/single-side/useSingleSideWithdraw';
import Spinner from '@/components/Spinner.vue';
import TokenIcon from '@/components/token/TokenIcon.vue';
import InputBalance from '@/components/form/InputBalance.vue';
import FilledListItem from '@/components/filledList/FilledListItem.vue';
import { SingleSideToken } from '../models/single-side-token';
import { PortfolioFarm } from '@/sdk/entities/portfolioFarm';
import Icon from '@/components/icon/Icon.vue';
import PriceImpactErrorMessage from '@/views/pages/liquidity/single-side/components/PriceImpactErrorMessage.vue';
import PortfolioReserveExceededMessage from '@/views/pages/liquidity/single-side/components/PortfolioReserveExceededMessage.vue';
import TokenWithdrawConfirmation from '@/views/pages/liquidity/single-side/withdraw/TokenWithdrawConfirmation.vue';
import { isNestedDialogOpen } from '@/composables/utils/useUtils';
import BaseInputNumber from '@/components/form/BaseInputNumber.vue';
import LpAmountFormat from '@/components/LpAmountFormat.vue';
import DestinationAddress from '@/components/DestinationAddress.vue';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { SingleSideWithdrawForm } from '@/store/modules/single-side/models/single-side-withdraw-form';
import { useSingleSideWithdrawSteps } from '@/store/modules/single-side/useSingleSideWithdrawSteps';
import WalletConnect from '@/views/layout/wallet/WalletConnect.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TokenWithdraw',
  props: {
    isShow: { type: Boolean },
    token: null,
    farm: null
  },
  emits: ["send-transaction"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'send-transaction'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  isShow: boolean;
  token: SingleSideToken;
  farm: PortfolioFarm;
}

const { t } = useI18n();
const { format } = formatNumbersMixin.methods;
const { state } = useStore();





const { singleSideWithdrawSteps, setSingleSideWithdrawStore } = useSingleSideWithdrawSteps();

const singleSideWithdrawStore = useSingleSideWithdraw();
const {
  setFarm,
  singleSideForm,
  $reset,
  setToken,
  setAmount,
  setMax,
  setAllowance,
  estimateWithdraw,
  doWithdraw,
} = singleSideWithdrawStore;

// INIT
setToken(props.token);
setFarm(props.farm);

const isPanelFocus = ref(false);
const inputValue = ref('');
const isConfirmationDialog = ref(false);

// CONFIRMATION DIALOG
const onCloseConfirmation = () => {
  isConfirmationDialog.value = false;
};
const onShowConfirmation = async () => {
  isConfirmationDialog.value = true;
  await estimateWithdraw();
};

// SET MAX
const isMaxDisabled = computed(() => {
  return (
    !singleSideForm.isWalletConnected ||
    singleSideForm.isBalanceLoading ||
    (ENABLE_FAKE_CARDANO_NETWORK && singleSideWithdrawSteps.isShown)
  );
});
const onSetMax = async () => {
  await setMax();
};

// NO FEE LIMIT
const noFeeLimit = computed(() => {
  const maxLength = 5;
  const noFeeLimitRaw = props.token.noFeeLimit;
  const integerPart = noFeeLimitRaw.decimalPlaces(0, BigNumber.ROUND_FLOOR);
  const integerPartLength = integerPart.toFixed().length;
  let decimals = maxLength - integerPartLength;
  decimals = decimals < 0 ? 0 : decimals;
  decimals = decimals > 2 ? 2 : decimals;

  return noFeeLimitRaw.toFixed(decimals, BigNumber.ROUND_DOWN);
});
const isNoFeeUnlimited = computed(() => !isFinite(+props.token.noFeeLimit));

// BALANCE
const hasBalanceError = computed(() => {
  return !!singleSideForm.errors['INSUFFICIENTLY_BALANCE'];
});
const balance = computed(() => (singleSideForm.balance ? BigNumber(singleSideForm.balance) : ''));

// INPUT
const isAmountMoreZero = computed(() => {
  return singleSideForm.input.amount && +singleSideForm.input.amount > 0;
});
const isInputDisabled = computed(() => {
  return (
    !singleSideForm.isWalletConnected ||
    singleSideForm.errors['BALANCE_REQUIRED'] ||
    (ENABLE_FAKE_CARDANO_NETWORK && singleSideWithdrawSteps.isShown)
  );
});
const maxFractionDigits = computed(() => props.token.portfolioToken.decimals);
const inputInLP = computed(() =>
  singleSideForm.isEstimateWithdrawRequestInProgress ? null : singleSideForm.amountLP,
);

// DESTINATION ADDRESS
const isDestinationAddress = computed(() => false);
const destinationAddress = ref('');

const stepsInProgress = computed(() => false);

// APPROVE BUTTON
const approveButtonLabel = `${t('approve')} ${t('lpTokens')}`;
const isAllowanceDisabled = computed(
  () =>
    hasBalanceError.value ||
    isInputDisabled.value ||
    singleSideForm.errors['AMOUNT_REQUIRED'] ||
    singleSideForm.errors['AMOUNT_IS_ZERO'] ||
    singleSideForm.isAllowanceRequestInProgress ||
    stepsInProgress.value,
);
const notAllowance = computed(
  () =>
    singleSideForm.input.token &&
    singleSideForm.hasAllowance !== undefined &&
    !singleSideForm.hasAllowance,
);
const onSetAllowance = async () => {
  await setAllowance();
};

// WITHDRAW BUTTON
const isButtonDisabled = computed(() => {
  return (
    !singleSideForm.isWalletConnected ||
    !singleSideForm.input.amount ||
    singleSideForm.isEstimateWithdrawRequestInProgress ||
    singleSideForm.errors['TOKEN_REQUIRED'] ||
    singleSideForm.errors['AMOUNT_REQUIRED'] ||
    singleSideForm.errors['AMOUNT_IS_ZERO'] ||
    singleSideForm.errors['BALANCE_REQUIRED'] ||
    singleSideForm.errors['INSUFFICIENTLY_BALANCE'] ||
    singleSideForm.errors['PRICE_IMPACT'] ||
    stepsInProgress.value ||
    (ENABLE_FAKE_CARDANO_NETWORK && singleSideWithdrawSteps.isShown)
  );
});

// ERRORS
const isPriceImpactError = computed(() => {
  return !!singleSideForm.errors['PRICE_IMPACT'];
});
const isExceededNoFeeLimit = computed(() => {
  return !!singleSideForm.errors['EXCEEDED_NO_FEE_LIMIT'] && !singleSideForm.errors['PRICE_IMPACT'];
});

const isPortfolioReserveExceeded = computed(() => false);
const amountDecrease = computed(() => '5');
const onDecreaseAmount = () => {
  setAmount(amountDecrease.value);
};

const onConfirmWithdraw = async () => {
  onCloseConfirmation();
  if (ENABLE_FAKE_CARDANO_NETWORK) {
    setSingleSideWithdrawStore(_.cloneDeep(singleSideWithdrawStore));
    emits('send-transaction');
    return;
  }

  await doWithdraw();
};

const onChangeInput = value => {
  setAmount(value);
};
const setPanelFocus = (value: boolean) => {
  isPanelFocus.value = value;
};

// Update token
watch(
  () => props.token,
  token => {
    if (token) {
      setToken(token);
    }
  },
);

// Update farm
watch(
  () => props.farm,
  farm => {
    if (farm) {
      setFarm(farm);
    }
  },
);

watch(
  singleSideForm.input,
  input => {
    inputValue.value = input.amount || '';
  },
  { immediate: true, deep: true },
);

watch(
  () => props.isShow,
  () => {
    $reset();
  },
);

return (_ctx: any,_cache: any) => {
  const _component_Skeleton = _resolveComponent("Skeleton")!
  const _component_Button = _resolveComponent("Button")!
  const _component_Dialog = _resolveComponent("Dialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", null, [
      (!_unref(singleSideForm).isWalletConnected)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(Icon, {
              name: "infoStar",
              size: "24",
              class: "mr-2"
            }),
            _createElementVNode("span", null, _toDisplayString(_unref(t)('singleSide.infoMessage.connectWallet')), 1)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          (_unref(isDestinationAddress))
            ? (_openBlock(), _createBlock(DestinationAddress, {
                key: 0,
                modelValue: destinationAddress.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((destinationAddress).value = $event))
              }, null, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          _createElementVNode("div", null, [
            _createElementVNode("div", {
              class: _normalizeClass(["flex-column input-panel input-panel-xl", { 'panel-focus': isPanelFocus.value, 'panel-error': _unref(hasBalanceError) }])
            }, [
              _createElementVNode("div", _hoisted_4, [
                (!_unref(state).layout.isMobile)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref(t)('singleSide.withdraw.inputPanelTitle')), 1))
                  : _createCommentVNode("", true),
                (_unref(singleSideForm).isWalletConnected)
                  ? (_openBlock(), _createBlock(InputBalance, {
                      key: 1,
                      "is-balance-error": _unref(hasBalanceError),
                      caption: _unref(t)('singleSide.withdraw.available.caption'),
                      tooltip: _unref(t)('singleSide.withdraw.available.tooltip'),
                      "balance-value": _unref(balance),
                      "balance-symbol": _unref(singleSideForm).input.token?.portfolioToken.symbol,
                      "is-max-disabled": _unref(isMaxDisabled),
                      onSetMax: onSetMax
                    }, null, 8, ["is-balance-error", "caption", "tooltip", "balance-value", "balance-symbol", "is-max-disabled"]))
                  : _createCommentVNode("", true)
              ]),
              _createVNode(BaseInputNumber, {
                "input-class": "input-xl",
                modelValue: inputValue.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((inputValue).value = $event)),
                disabled: _unref(isInputDisabled),
                "max-fraction-digits": _unref(maxFractionDigits),
                onInput: _cache[2] || (_cache[2] = ($event: any) => (onChangeInput($event))),
                onFocus: _cache[3] || (_cache[3] = ($event: any) => (setPanelFocus(true))),
                onBlur: _cache[4] || (_cache[4] = ($event: any) => (setPanelFocus(false))),
                min: 0
              }, null, 8, ["modelValue", "disabled", "max-fraction-digits"]),
              _createElementVNode("div", _hoisted_6, [
                (inputValue.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      (_unref(inputInLP))
                        ? (_openBlock(), _createBlock(LpAmountFormat, {
                            key: 0,
                            "lp-amount": _unref(inputInLP),
                            approx: ""
                          }, null, 8, ["lp-amount"]))
                        : (_openBlock(), _createBlock(_component_Skeleton, {
                            key: 1,
                            width: "auto"
                          }))
                    ], 64))
                  : _createCommentVNode("", true)
              ])
            ], 2)
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("div", null, [
              _createVNode(FilledListItem, {
                caption: _unref(t)('singleSide.noFeeLimit.caption'),
                tooltip: _unref(t)('singleSide.noFeeLimit.tooltip.withdraw'),
                color: _unref(isExceededNoFeeLimit) || _unref(isPriceImpactError) ? 'warning' : ''
              }, {
                data: _withCtx(() => [
                  (!_unref(isNoFeeUnlimited))
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "flex align-items-center clickable",
                        onClick: _cache[5] || (_cache[5] = ($event: any) => (_unref(setAmount)(_unref(noFeeLimit))))
                      }, [
                        _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(format)(_unref(noFeeLimit))), 1),
                        _createVNode(TokenIcon, {
                          "icon-src": __props.token.portfolioToken.tokenIconUrl,
                          size: "16",
                          symbol: __props.token.portfolioToken.symbol
                        }, null, 8, ["icon-src", "symbol"])
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(_unref(t)('unlimited')), 1))
                ]),
                _: 1
              }, 8, ["caption", "tooltip", "color"]),
              (_unref(isExceededNoFeeLimit))
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(_unref(t)('singleSide.infoMessage.payFee', {
                  fee: _unref(format)(_unref(singleSideForm).lossInUSD.toFixed(2)),
                })), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", null, [
              (!_unref(singleSideForm).isWalletConnected)
                ? (_openBlock(), _createBlock(WalletConnect, {
                    key: 0,
                    ref: "wallet",
                    "button-class": "w-full button-primary button-lg"
                  }, null, 512))
                : (!_unref(ENABLE_FAKE_CARDANO_NETWORK) && _unref(notAllowance) && _unref(isAmountMoreZero))
                  ? (_openBlock(), _createBlock(_component_Button, {
                      key: 1,
                      class: "w-full button-primary button-lg",
                      disabled: _unref(isAllowanceDisabled),
                      onClick: onSetAllowance
                    }, {
                      default: _withCtx(() => [
                        (_unref(singleSideForm).isAllowanceRequestInProgress)
                          ? (_openBlock(), _createBlock(Spinner, {
                              key: 0,
                              size: "24",
                              class: "mr-s2"
                            }))
                          : _createCommentVNode("", true),
                        _createTextVNode(" " + _toDisplayString(approveButtonLabel))
                      ]),
                      _: 1
                    }, 8, ["disabled"]))
                  : (_openBlock(), _createBlock(_component_Button, {
                      key: 2,
                      class: "w-full button-primary button-lg",
                      disabled: _unref(isButtonDisabled),
                      onClick: onShowConfirmation
                    }, {
                      default: _withCtx(() => [
                        (_unref(singleSideForm).isEstimateWithdrawRequestInProgress)
                          ? (_openBlock(), _createBlock(Spinner, {
                              key: 0,
                              size: "24",
                              class: "mr-s2"
                            }))
                          : _createCommentVNode("", true),
                        _createTextVNode(" " + _toDisplayString(_unref(t)('singleSide.withdraw.buttonLabel')), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"]))
            ])
          ])
        ])
      ]),
      (_unref(isPriceImpactError))
        ? (_openBlock(), _createBlock(PriceImpactErrorMessage, {
            key: 1,
            class: "mt-s3"
          }))
        : _createCommentVNode("", true),
      (_unref(isPortfolioReserveExceeded))
        ? (_openBlock(), _createBlock(PortfolioReserveExceededMessage, {
            key: 2,
            onClick: onDecreaseAmount,
            "token-symbol": __props.token.portfolioToken.symbol ?? '',
            "chain-id": __props.token.portfolioToken.chainId,
            amount: _unref(amountDecrease),
            class: "mt-s3"
          }, null, 8, ["token-symbol", "chain-id", "amount"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_Dialog, {
      visible: isConfirmationDialog.value,
      "onUpdate:visible": _cache[6] || (_cache[6] = ($event: any) => ((isConfirmationDialog).value = $event)),
      modal: true,
      dismissableMask: !_unref(isNestedDialogOpen),
      "content-class": _unref(state).layout.isDesktop ? 'dialog-content-scrollable -mr-s3 pr-s2' : ''
    }, {
      header: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(t)('singleSide.confirmDialog.withdraw.header', { portfolioName: __props.farm.portfolio.name })), 1),
        _hoisted_12
      ]),
      default: _withCtx(() => [
        _createVNode(TokenWithdrawConfirmation, {
          "token-form": (_unref(singleSideForm) as SingleSideWithdrawForm),
          onCancel: onCloseConfirmation,
          onConfirm: onConfirmWithdraw
        }, null, 8, ["token-form"])
      ]),
      _: 1
    }, 8, ["visible", "dismissableMask", "content-class"])
  ], 64))
}
}

})